import React, { useState, useEffect } from 'react';
import { db, storage, functions } from './firebase.js';
import { httpsCallable } from 'firebase/functions';
import { collection, onSnapshot, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
// eslint-disable-next-line
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const AnimalIdentification = ({ user, isAdmin, isMobile }) => {
  const [animals, setAnimals] = useState([]);
  const [editingAnimal, setEditingAnimal] = useState(null);
  const [flippedCard, setFlippedCard] = useState(null);
  const [imageErrors, setImageErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [status, setStatus] = useState(null);
  const [reclassifying, setReclassifying] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');

  const animalTypes = ['bird', 'insect', 'mammal', 'fish', 'reptile', 'amphibian', 'other'];
  const typeDisplayMap = {
    bird: 'Birds',
    insect: 'Insects',
    mammal: 'Mammals',
    fish: 'Fish',
    reptile: 'Reptiles',
    amphibian: 'Amphibians',
    other: 'Other',
  };

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'fauna'), (snapshot) => {
      const fetchedAnimals = snapshot.docs.map((doc) => {
        const data = doc.data();
        const displayImageUrl = data.imageUrl || (data.images && data.images[0]) || 'undefined';
        return {
          id: doc.id,
          ...data,
          displayImageUrl,
          type: normalizeType(data.type), // Consistent normalization
        };
      });
      setAnimals(fetchedAnimals);
      setLoading(false);
    }, (err) => {
      console.error('Error fetching fauna:', err);
      setError(err.message);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const normalizeType = (type) => {
    return type ? type.toLowerCase().replace(/s$/, '') : 'other';
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadError(null);
    setStatus('Uploading...');
    try {
      const storageRef = ref(storage, `fauna_images/${Date.now()}_${file.name}`);
      const imageUrl = await getDownloadURL(storageRef);

      setStatus('Processing...');
      const identifyAnimal = httpsCallable(functions, 'apiIdentifyAnimal');
      const result = await identifyAnimal({ imageUrl });
      let animalData = result.data || {};
      if (animalData.error || !animalData.common_name) {
        animalData = {
          type: 'other',
          common_name: 'Unclassified',
          scientific_name: 'Unknown',
          genus: 'Unknown',
          species: 'Unknown',
          habitat: 'Unknown',
          behavior: 'Unknown',
          endangered: 'No',
          imageUrl,
          images: [imageUrl],
          created_by: user?.uid,
        };
      } else {
        animalData = {
          ...animalData,
          endangered: animalData.endangered ? 'Yes' : 'No',
          imageUrl,
          images: [imageUrl],
          created_by: user?.uid,
          type: normalizeType(animalData.type),
        };
      }

      setStatus('Creating card...');
	  // eslint-disable-next-line
      const docRef = await addDoc(collection(db, 'fauna'), animalData);
      setStatus(null);
    } catch (err) {
      console.error('Upload/Identification error:', err);
      setUploadError(err.message || 'Internal error');
      setStatus(null);
    }
  };

  const reclassifyUncategorized = async () => {
    setReclassifying(true);
    setStatus('Reclassifying animals... This may take a minute.');
    try {
      const uncategorized = animals.filter((a) => a.type === 'other');
      for (const animal of uncategorized) {
        if (!animal.imageUrl) continue;
        const identifyAnimal = httpsCallable(functions, 'apiIdentifyAnimal');
        const result = await identifyAnimal({ imageUrl: animal.imageUrl });
        if (!result.data?.error && result.data?.common_name) {
          await updateDoc(doc(db, 'fauna', animal.id), {
            type: normalizeType(result.data.type),
            ...result.data,
            endangered: result.data.endangered ? 'Yes' : 'No',
          });
        }
      }
      setStatus('Reclassification complete! Refresh if needed.');
    } catch (err) {
      setStatus(`Error during reclassification: ${err.message}`);
    } finally {
      setReclassifying(false);
    }
  };

  const handleEdit = (animal) => {
    setEditingAnimal({ ...animal });
  };

  const handleSaveEdit = async () => {
    if (!isAdmin || !editingAnimal?.id) return;
    await updateDoc(doc(db, 'fauna', editingAnimal.id), {
      ...editingAnimal,
      type: normalizeType(editingAnimal.type),
    });
    setEditingAnimal(null);
  };

  const handleDelete = async (id) => {
    if (!isAdmin || !id) return;
    await deleteDoc(doc(db, 'fauna', id));
  };

  const toggleFlip = (id) => setFlippedCard((prev) => (prev === id ? null : id));
  const handleImageError = (id, url) => setImageErrors((prev) => ({ ...prev, [id]: true }));

  const handleCategoryChange = (e) => setSelectedCategory(e.target.value);

  const filteredAnimals = animals.filter((animal) =>
    (selectedCategory === 'All' || animal.type === selectedCategory) &&
    (!imageErrors[animal.id] || animal.displayImageUrl)
  );

  if (loading) return <div className="text-center text-xl mt-6">Loading...</div>;
  if (error) return <div className="text-center text-red-500 mt-6">Error: {error}</div>;

  return (
    <div className={`flora-page ${isMobile ? 'flora-page-mobile' : ''}`}>
      <h2 className="text-2xl font-bold text-center mb-6">Animal and Wildlife Identification</h2>
      {user && (
        <>
          <p className="text-center text-gray-600 mb-4">Upload an image to have it identified. Clicking on a card will reveal more information.</p>
          {uploadError && <div className="text-center text-red-500 mb-4">Upload error: {uploadError}</div>}
          {status && <div className="text-center text-blue-500 mb-4">{status}</div>}
          <label className="header-nav-button block w-full max-w-[10rem] ml-0 mb-6 text-center cursor-pointer">
            Upload image
            <input type="file" hidden onChange={handleFileChange} accept="image/*" />
          </label>
          {isAdmin && (
            <button
              onClick={reclassifyUncategorized}
              disabled={reclassifying}
              className="header-nav-button block w-full max-w-[10rem] ml-0 mb-6 text-center"
            >
              {reclassifying ? 'Reclassifying...' : 'Reclassify'}
            </button>
          )}
        </>
      )}
      <select
        value={selectedCategory}
        onChange={handleCategoryChange}
        className="block w-full max-w-[15rem] ml-0 mb-6 p-2 border border-gray-300 rounded"
      >
        <option value="All">All Categories</option>
        {animalTypes.map((type) => (
          <option key={type} value={type}>
            {typeDisplayMap[type]}
          </option>
        ))}
      </select>

      {animals.length === 0 && <p className="text-center text-gray-500">No animals found. Upload a new image to start.</p>}
      <div className="flora-grid">
        {filteredAnimals.map((animal) => (
          <div key={animal.id} className="card-container">
            <div className={`card ${flippedCard === animal.id ? 'flipped' : ''}`} onClick={() => toggleFlip(animal.id)}>
              <div className="card-front">
                <div className="card-image-area">
                  {imageErrors[animal.id] || !animal.displayImageUrl ? (
                    <img src="https://placehold.co/400x320/e5e7eb/6b7280?text=No+Image&font=roboto" alt="Unavailable" />
                  ) : (
                    <img
                      src={animal.displayImageUrl}
                      alt={animal.common_name}
                      onError={() => handleImageError(animal.id, animal.displayImageUrl)}
                    />
                  )}
                </div>
                <div className="card-info">
                  <h3>{animal.common_name}</h3>
                  <p>Scientific: {animal.scientific_name}</p>
                </div>
                {user && (
                  <div className="card-actions">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleEdit(animal); }}
                      className="admin-users-action-button"
                    >
                      Edit
                    </button>
                    {isAdmin && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDelete(animal.id); }}
                        className="admin-users-delete-button"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                )}
              </div>
              <div className="card-back bg-gray-300 text-left">
                <p className="text-sm"><strong>Genus:</strong> {animal.genus}</p>
                <p className="text-sm"><strong>Species:</strong> {animal.species}</p>
                <p className="text-sm"><strong>Habitat:</strong> {animal.habitat}</p>
                <p className="text-sm"><strong>Behavior:</strong> {animal.behavior}</p>
                <p className="text-sm"><strong>Endangered:</strong> {animal.endangered}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editingAnimal && (
        <div className={`modal-overlay ${isMobile ? 'modal-content-mobile' : ''}`}>
          <div className="modal-content">
            <h2 className="modal-title">Edit Animal</h2>
            <input
              type="text"
              value={editingAnimal.type || ''}
              onChange={(e) => setEditingAnimal({ ...editingAnimal, type: e.target.value })}
              placeholder="Type (e.g., bird)"
              className="modal-input h-16"
            />
            <input
              type="text"
              value={editingAnimal.common_name || ''}
              onChange={(e) => setEditingAnimal({ ...editingAnimal, common_name: e.target.value })}
              placeholder="Common Name"
              className="modal-input h-16"
            />
            <input
              type="text"
              value={editingAnimal.scientific_name || ''}
              onChange={(e) => setEditingAnimal({ ...editingAnimal, scientific_name: e.target.value })}
              placeholder="Scientific Name"
              className="modal-input h-16"
            />
            <input
              type="text"
              value={editingAnimal.genus || ''}
              onChange={(e) => setEditingAnimal({ ...editingAnimal, genus: e.target.value })}
              placeholder="Genus"
              className="modal-input h-16"
            />
            <input
              type="text"
              value={editingAnimal.species || ''}
              onChange={(e) => setEditingAnimal({ ...editingAnimal, species: e.target.value })}
              placeholder="Species"
              className="modal-input h-16"
            />
            <textarea
              value={editingAnimal.habitat || ''}
              onChange={(e) => setEditingAnimal({ ...editingAnimal, habitat: e.target.value })}
              placeholder="Habitat"
              className="modal-textarea h-24"
            />
            <textarea
              value={editingAnimal.behavior || ''}
              onChange={(e) => setEditingAnimal({ ...editingAnimal, behavior: e.target.value })}
              placeholder="Behavior"
              className="modal-textarea h-24"
            />
            <input
              type="text"
              value={editingAnimal.endangered || ''}
              onChange={(e) => setEditingAnimal({ ...editingAnimal, endangered: e.target.value })}
              placeholder="Endangered (Yes/No)"
              className="modal-input h-16"
            />
            <div className="modal-button-group">
              <button onClick={handleSaveEdit} className="modal-button modal-button-primary">Save</button>
              <button onClick={() => setEditingAnimal(null)} className="modal-button modal-button-secondary">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnimalIdentification;